import { MessageBar, MessageBarBody, MessageBarActions, Button } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

/**
 * Display multiple errors in a single MessageBar with grouping and counts
 * @param {Array} errors - Array of error objects with {message, type, ...}
 * @param {Function} onDismiss - Callback when all errors are dismissed
 */
export function ErrorMessagesBar({ errors = [], onDismiss }) {
  const { t } = useTranslation();

  if (!errors || errors.length === 0) {
    return null;
  }

  // Group identical messages to show counts
  const grouped = errors.reduce((acc, err) => {
    const existing = acc.find((g) => g.message === err.message);
    if (existing) {
      existing.count += 1;
      existing.items.push(err);
    } else {
      acc.push({ message: err.message, type: err.type, items: [err], count: 1 });
    }
    return acc;
  }, []);

  // Choose most severe type to drive the bar style
  const severity = grouped.some((g) => g.type === "error")
    ? "error"
    : grouped.some((g) => g.type === "warning")
      ? "warning"
      : grouped.some((g) => g.type === "success")
        ? "success"
        : "info";

  const messageText =
    grouped.length === 1
      ? `${grouped[0].message}${grouped[0].count > 1 ? ` (${grouped[0].count}x)` : ""}`
      : grouped.map((g) => `${g.message}${g.count > 1 ? ` (${g.count}x)` : ""}`).join("\n");

  const handleDismiss = () => {
    if (onDismiss) {
      // Pass back all involved error items so callers can clear them
      const allItems = grouped.flatMap((g) => g.items);
      onDismiss(allItems);
    }
  };

  return (
    <MessageBar intent={severity} style={{ whiteSpace: "pre-line" }}>
      <MessageBarBody>{messageText}</MessageBarBody>
      <MessageBarActions
        containerAction={
          <Button
            onClick={handleDismiss}
            appearance="transparent"
            icon={<DismissRegular />}
            aria-label="Close"
            size="small"
          />
        }
      />
    </MessageBar>
  );
}
